<template>
  <b-container class="question-list">
    <b-row>
      <b-col>
        <div class="page-header">Questions</div>

        <b-btn class="float-right" variant="primary" @click="createQuestion">
          Create Question
        </b-btn>
      </b-col>
    </b-row>

    <v-wait :for="loaderId">
      <template slot="waiting">
        <HalfCircleSpinner />
        <div class="spinner-message my-3 text-center">
          Loading the questions...
        </div>
      </template>
      <DataTable
        ref="dataTable"
        :show-filters="true"
        :show-pager="true"
        :items="filteredQuestions"
        :filters="filters"
        :filter-title="'Filter Questions'"
        :fields="fields"
        :no-local-sorting="false"
        :busy="loadingStudies"
      >
        <!-- template for filters -->

        <template slot="filters">
          <b-container class="mb-3">
            <b-row>
              <b-col lg="6" md="12">
                <b-row>
                  <b-col cols="12">
                    <label>Prompt</label>
                    <b-form-input v-model="filters.prompt.value" />
                  </b-col>
                  <b-col cols="12" class="mt-3">
                    <label>Label</label>
                    <b-form-input v-model="filters.label.value" />
                  </b-col>
                </b-row>
              </b-col>
              <b-col lg="6" md="12" class="mt-3 mt-lg-0">
                <b-row>
                  <b-col cols="12">
                    <label>Society</label>
                    <b-form-select
                      v-model="filters.society_id.value"
                      :options="societyOptions"
                    />
                  </b-col>
                  <b-col cols="12" class="mt-3">
                    <label>&nbsp;</label>
                    <div class="position-relative">
                      <b-form-select
                        v-model="filters.study_id.value"
                        :options="studiesOptions"
                        :disabled="loadingStudies"
                      >
                        <template slot="first">
                          <option :value="null" disabled>
                            -- Select --
                          </option>
                        </template>
                      </b-form-select>
                      <div
                        v-if="loadingStudies"
                        class="spinner-wrapper text-center py-2"
                      >
                        <b-spinner small />
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row class="mt-3">
              <b-col cols="12" class="text-right">
                <b-button
                  variant="primary"
                  @click="onGenerateCSV"
                  :disabled="loadingCsv"
                >
                  <b-spinner v-if="loadingCsv" small />
                  <span v-else>Generate CSV</span>
                </b-button>
              </b-col>
            </b-row>
          </b-container>
        </template>

        <!-- templates for custom columns -->

        <template slot="type" slot-scope="data">
          {{ data.item.type }}
          <b-badge
            class="required-badge"
            variant="danger"
            v-if="data.item.required"
          >
            *
          </b-badge>
        </template>

        <template slot="prompt" slot-scope="data">
          <strong>{{ data.item.label }}:</strong>
          <br />
          {{ data.item.prompt }}
        </template>

        <template slot="sectionsCount" slot-scope="data">
          <b-button
            v-if="data.item.sectionsCount > 0"
            variant="v2_link"
            class="py-0 px-2 v2_font_size_18 min-width-unset"
            @click="idToShowQuestionnaires = data.item.id"
          >
            {{ data.item.sectionsCount }}
          </b-button>
          <span v-else class="py-0 px-2">{{ data.item.sectionsCount }}</span>
        </template>
        <template slot="actions" slot-scope="data">
          <b-dropdown
            split
            variant="info"
            size="sm"
            text="Edit"
            @click.stop="editQuestion(data.item.id)"
          >
            <b-dropdown-item-button
              @click.stop="onCopyQuestion(data.item.id)"
              class="btn-sm"
              :disabled="!!loadingCopyQuestionId"
            >
              <b-spinner v-if="loadingCopyQuestionId === data.item.id" small />
              <span v-else>Copy</span>
            </b-dropdown-item-button>
            <b-dropdown-item-button
              @click.stop="onDeleteQuestion(data.item.id)"
              class="btn-sm"
            >
              Delete
            </b-dropdown-item-button>
          </b-dropdown>
        </template>
      </DataTable>
    </v-wait>

    <modal-edit-form
      v-if="showModal"
      :initial-question="editedQuestion"
      ref="modal"
      @modalCancel="onModalCancel"
      @questionSaved="onQuestionSaved"
    />
    <b-modal
      :visible="!!idToShowQuestionnaires"
      size="xl"
      ok-only
      ok-title="Close"
      title="Question Questionnaires"
      @hidden="idToShowQuestionnaires = null"
    >
      <questionnaires-list-by-question
        v-if="!!idToShowQuestionnaires"
        :question-id="idToShowQuestionnaires"
      />
    </b-modal>
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import DataTable from '@/components/DataTable.vue';
import Api from '@/providers/Api.js';
import { HalfCircleSpinner } from 'epic-spinners';
import ModalEditForm from '@/components/question/ModalEditForm';
import Help from '@/providers/Help';
import { CHART_TYPES, QUESTION_TYPES } from '@/store';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import { csvEscape } from '@/helpers/csvHelper';
import QuestionnairesListByQuestion from '@/components/question/QuestionnairesListByQuestion';

export default {
  name: 'List',
  components: {
    DataTable,
    HalfCircleSpinner,
    ModalEditForm,
    QuestionnairesListByQuestion,
  },
  data: function () {
    return {
      questions: [],
      editedQuestion: null,
      showModal: false,
      filters: {
        prompt: { field: 'prompt', type: 'string', value: null },
        label: { field: 'label', type: 'string', value: null },
        society_id: { field: 'society_id', type: 'string', value: null },
        study_id: { field: 'study_id', type: 'string', value: null },
      },
      fields: [
        { key: 'id', sortable: true },
        {
          key: 'prompt',
          sortable: true,
          custom: true,
          label: 'Label / Prompt',
        },
        {
          key: 'sectionsCount',
          sortable: false,
          label: 'Questionnaires',
          custom: true,
        },
        { key: 'type', sortable: true, custom: true, label: 'Type / Required' },
        { key: 'actions', sortable: false, custom: true },
      ],
      loadingCsv: false,
      loadingStudies: false,
      loadingCopyQuestionId: null,
      studies: [],
      idToShowQuestionnaires: null,
    };
  },
  created: function () {
    this.refreshData();
  },
  computed: {
    societyOptions: function () {
      return this.$store.state.societyOptions;
    },
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    filteredQuestions: function () {
      const filteredByPrompt =
        this.filters.prompt && !!this.filters.prompt.value;
      const filteredByLabel = this.filters.label && !!this.filters.label.value;
      const filteredBySocietyId =
        this.filters.society_id && !!this.filters.society_id.value;
      const filteredByStudyId =
        this.filters.study_id && !!this.filters.study_id.value;
      if (
        filteredByPrompt ||
        filteredByLabel ||
        filteredBySocietyId ||
        filteredByStudyId
      ) {
        return this.questions.filter((q) => {
          let isMatch = true;
          if (filteredByPrompt) {
            isMatch &= q.prompt && q.prompt
              .toLowerCase()
              .includes(this.filters.prompt.value.toLowerCase());
          }
          if (filteredByLabel) {
            isMatch &= q.label && q.label
              .toLowerCase()
              .includes(this.filters.label.value.toLowerCase());
          }
          if (filteredByStudyId) {
            isMatch &=
              Array.isArray(q.sections) &&
              q.sections.some(
                ({ questionnaire_id }) =>
                  questionnaire_id === this.filters.study_id.value
              );
          } else if (filteredBySocietyId) {
            isMatch &=
              Array.isArray(q.sections) &&
              q.sections.some(({ questionnaire_id }) =>
                this.studies.some(({ id }) => id === questionnaire_id)
              );
          }
          return isMatch;
        });
      } else {
        return this.questions;
      }
    },
    studiesOptions: function () {
      return this.studies.map(({ id, title, period }) => ({
        value: id,
        text: `${title} ${period}`,
      }));
    },
  },
  methods: {
    refreshData: async function () {
      this.$wait.start(this.loaderId);
      try {
        this.questions = await Api.getAllQuestions(this);
      } catch (error) {
        this.$toasted.error('Upload data error', { duration: 3000 });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    createQuestion: function () {
      this.openEditModal({
        id: null,
        prompt: '[New Question]',
        type: QUESTION_TYPES.SLIDER,
        variables: [
          {
            uid: Date.now(),
            order: 1,
            name: 'NEW_VARIABLE',
            input_label: '[New Variable]',
            visible: true,
            response: {},
          },
        ],
        chart: {
          events: {
            render: () => this.$emit('chart-rendered'),
          },
          chart_type: CHART_TYPES.HISTOGRAM,
          chart_config: {},
        },
      });
    },
    editQuestion: function (id) {
      const question = this.questions.find((q) => q.id === id);
      this.openEditModal(question);
    },
    openEditModal: function (question = null) {
      this.editedQuestion = question;
      this.showModal = true;
    },
    deleteQuestion: function (id) {
      Api.deleteQuestion(id).then(() => {
        const index = this.questions.findIndex((q) => q.id === id);
        this.questions.splice(index, 1);
        this.$toasted.success('Question deleted', { duration: 3000 });
        this.$refs.dataTable.refresh();
      });
    },
    onDeleteQuestion: function (id) {
      Help.confirm.show('Permanently delete this question?', 'Delete', () => {
        this.deleteQuestion(id);
      });
    },
    onModalCancel: function () {
      this.editedQuestion = null;
      this.showModal = false;
    },
    onQuestionSaved: function (question) {
      const index = this.questions.findIndex((q) => q.id === question.id);
      if (index !== -1) {
        this.$set(this.questions, index, question);
      } else {
        this.$set(this.questions, this.questions.length, question);
      }
    },
    refreshStudies: async function () {
      this.loadingStudies = true;
      try {
        this.studies = await Api.adminGetAllSurveys(
          this.filters.society_id.value
        );
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.loadingStudies = false;
      }
    },
    onGenerateCSV: function () {
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += 'RowNum,id,Type,Label,Preamble,Prompt,Explanation\n';
      csvContent += this.filteredQuestions
        .map(({ id, type, label, preamble, prompt, explanation }, index) =>
          [
            index + 1,
            id,
            `"${type}"`,
            `"${csvEscape(label)}"`,
            `"${csvEscape(preamble)}"`,
            `"${csvEscape(prompt)}"`,
            `"${csvEscape(explanation)}"`,
          ].join(',')
        )
        .join('\n');
      let encodedUri = encodeURI(csvContent);
      let link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'Questions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onCopyQuestion: function (id) {
      Help.confirm.show(
        'Are you sure you want to copy this question?',
        'Accept',
        () => {
          this.copyQuestion(id);
        }
      );
    },
    copyQuestion: async function (id) {
      this.loadingCopyQuestionId = id;
      try {
        const { question, errors } = await Api.copyQuestion(id);
        this.questions.push(question);
        if (Array.isArray(errors) && errors.length) {
          this.$toasted.info('Question copied with some errors!', {
            duration: 3000,
          });
          this.$toasted.error(errors.join('<br/>'), { duration: 9000 });
        } else {
          this.$toasted.success('Question copied successfully!', {
            duration: 3000,
          });
        }
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted, { duration: 9000 });
      } finally {
        this.loadingCopyQuestionId = null;
      }
    },
  },
  watch: {
    'filters.society_id.value': function () {
      this.$set(this.filters.study_id, 'value', null);
      this.refreshStudies();
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss" scoped>
.page-header {
  display: inline-block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  line-height: 1.2;
  font-size: 2rem;
  font-family: 'Questrial', arial, helvetica, sans-serif;
}

.spinner-wrapper {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  background: lightgray;
}

.min-width-unset {
  min-width: unset;
}
</style>
